// Create / edit an attribute type. Key is immutable once created — it's the
// stable identifier products/variants reference, so it's disabled in edit mode.

import { AttributeTypeAdmin, useCreateAttributeTypeApi, useUpdateAttributeTypeApi } from 'apis/product-attributes';
import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  useToast,
} from 'design-system';
import { useEffect, useState } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  type?: AttributeTypeAdmin; // present -> edit, absent -> create
}

interface FormState {
  key: string;
  label: string;
  isIdentityDefault: boolean;
  sortOrder: string;
  isActive: boolean;
}

const emptyForm = (): FormState => ({
  key: '',
  label: '',
  isIdentityDefault: false,
  sortOrder: '',
  isActive: true,
});

const fromType = (t: AttributeTypeAdmin): FormState => ({
  key: t.key,
  label: t.label,
  isIdentityDefault: t.isIdentityDefault,
  sortOrder: String(t.sortOrder),
  isActive: t.isActive,
});

export const TypeFormModal = ({ isOpen, onClose, type }: Props) => {
  const toast = useToast();
  const isEdit = !!type;
  const [form, setForm] = useState<FormState>(emptyForm());

  useEffect(() => {
    if (isOpen) setForm(type ? fromType(type) : emptyForm());
  }, [isOpen, type]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const onDone = () => {
    toast({ title: `Attribute type ${isEdit ? 'updated' : 'created'}`, status: 'success' });
    onClose();
  };
  const onError = (error: any) => {
    // hooks toast server messages; this is the network-error fallback
    if (!error?.response?.data?.message) {
      toast({ title: `Failed to ${isEdit ? 'update' : 'create'} attribute type`, status: 'error' });
    }
  };

  const createApi = useCreateAttributeTypeApi({ onSuccess: onDone, onError });
  const updateApi = useUpdateAttributeTypeApi({ onSuccess: onDone, onError });
  const isSaving = createApi.isLoading || updateApi.isLoading;

  const submit = () => {
    if (!form.label.trim()) {
      toast({ title: 'Label is required', status: 'warning' });
      return;
    }
    if (!isEdit && !form.key.trim()) {
      toast({ title: 'Key is required', status: 'warning' });
      return;
    }
    const sortOrderNum = form.sortOrder.trim() ? Number(form.sortOrder) : undefined;
    if (sortOrderNum !== undefined && !Number.isFinite(sortOrderNum)) {
      toast({ title: 'Sort order must be a number', status: 'warning' });
      return;
    }

    if (isEdit && type) {
      updateApi.mutate({
        id: type.id,
        data: {
          label: form.label.trim(),
          isIdentityDefault: form.isIdentityDefault,
          sortOrder: sortOrderNum,
          isActive: form.isActive,
        },
      });
    } else {
      createApi.mutate({
        key: form.key.trim(),
        label: form.label.trim(),
        isIdentityDefault: form.isIdentityDefault,
        sortOrder: sortOrderNum,
        isActive: form.isActive,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='md'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? 'Edit Attribute Type' : 'New Attribute Type'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired mb='3'>
            <FormLabel fontSize='sm'>Label</FormLabel>
            <Input value={form.label} onChange={(e) => set('label', e.target.value)} autoFocus />
          </FormControl>
          <FormControl isRequired mb='3'>
            <FormLabel fontSize='sm'>Key</FormLabel>
            <Input
              value={form.key}
              onChange={(e) => set('key', e.target.value)}
              isDisabled={isEdit}
              placeholder='e.g. brand'
            />
            <FormHelperText>lowercase slug, immutable</FormHelperText>
          </FormControl>
          <FormControl mb='3'>
            <FormLabel fontSize='sm'>Sort order</FormLabel>
            <Input type='number' value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
          </FormControl>
          <Flex align='center' gap='2' mb='2'>
            <Switch
              isChecked={form.isIdentityDefault}
              onChange={(e) => set('isIdentityDefault', e.target.checked)}
              colorScheme='green'
            />
            <Text fontSize='sm'>Identity default</Text>
          </Flex>
          <Flex align='center' gap='2'>
            <Switch isChecked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} colorScheme='green' />
            <Text fontSize='sm'>Active</Text>
          </Flex>
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme='red' onClick={submit} isLoading={isSaving}>
            {isEdit ? 'Save Changes' : 'Create Type'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
